import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Row, Col, Select, Space, Spin, Empty, InputNumber, Button, Tooltip, message, DatePicker, Divider, Alert } from 'antd';
import {
  LineChartOutlined,
  SettingOutlined,
  SyncOutlined,
  SearchOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
  TimeScale,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { Line } from 'react-chartjs-2';
import { agentsAPI } from '../services/api';
import socketService from '../services/socket';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler,
  TimeScale
);

const { RangePicker } = DatePicker;

const parseTs = (ts) => {
  if (!ts) return new Date();
  if (typeof ts !== 'string') return new Date(ts);
  if (ts.endsWith('Z') || ts.includes('+')) return new Date(ts);
  return new Date(ts.replace(' ', 'T') + 'Z');
};

const bytesPerSecToMb = (bps) => {
  const n = Number(bps);
  if (!Number.isFinite(n) || n < 0) return null;
  return parseFloat((n / (1024 * 1024)).toFixed(3));
};

const TIME_RANGES = [
  { label: '5 min', value: 5 },
  { label: '15 min', value: 15 },
  { label: '30 min', value: 30 },
  { label: '1 hour', value: 60 },
  { label: '3 hours', value: 180 },
  { label: '6 hours', value: 360 },
  { label: '12 hours', value: 720 },
  { label: '24 hours', value: 1440 },
];

const makeDiskIoChartOptions = (title, xBounds = {}, yMax) => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 300 },
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: { display: true, position: 'top', labels: { boxWidth: 12, padding: 8, font: { size: 11 } } },
    title: { display: true, text: title, font: { size: 13 } },
    tooltip: {
      callbacks: {
        label: (ctx) => {
          const v = ctx.parsed.y;
          return `${ctx.dataset.label}: ${v != null ? v.toFixed(3) : '-'} MB/s`;
        },
      },
    },
  },
  scales: {
    x: {
      type: 'time',
      time: {
        tooltipFormat: 'dd/MM/yyyy HH:mm:ss',
        displayFormats: { second: 'HH:mm:ss', minute: 'HH:mm', hour: 'dd/MM HH:mm', day: 'dd/MM' },
      },
      ...(xBounds.min ? { min: xBounds.min } : {}),
      ...(xBounds.max ? { max: xBounds.max } : {}),
      ticks: { maxTicksLimit: 12, font: { size: 10 } },
      grid: { display: false },
    },
    y: {
      min: 0,
      title: { display: true, text: 'MB/s', font: { size: 10 } },
      ...(yMax ? { max: yMax } : {}),
      ticks: {
        font: { size: 10 },
        callback: (v) => (v >= 1 ? v.toFixed(1) : v.toFixed(2)),
      },
      grid: { color: 'rgba(0,0,0,0.04)' },
    },
  },
});

const makeLoadChartOptions = (title, xBounds = {}, yMax) => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 300 },
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: { display: true, position: 'top', labels: { boxWidth: 12, padding: 8, font: { size: 11 } } },
    title: { display: true, text: title, font: { size: 13 } },
    tooltip: {
      callbacks: {
        label: (ctx) => {
          const v = ctx.parsed.y;
          return `${ctx.dataset.label}: ${v != null ? v.toFixed(2) : '-'}`;
        },
      },
    },
  },
  scales: {
    x: {
      type: 'time',
      time: {
        tooltipFormat: 'dd/MM/yyyy HH:mm:ss',
        displayFormats: { second: 'HH:mm:ss', minute: 'HH:mm', hour: 'dd/MM HH:mm', day: 'dd/MM' },
      },
      ...(xBounds.min ? { min: xBounds.min } : {}),
      ...(xBounds.max ? { max: xBounds.max } : {}),
      ticks: { maxTicksLimit: 12, font: { size: 10 } },
      grid: { display: false },
    },
    y: {
      min: 0,
      ...(yMax ? { max: yMax } : { suggestedMax: 2 }),
      ticks: { font: { size: 10 } },
      grid: { color: 'rgba(0,0,0,0.04)' },
    },
  },
});

const makeChartOptions = (title, yMax = 100, xBounds = {}) => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 300 },
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: { display: true, position: 'top', labels: { boxWidth: 12, padding: 8, font: { size: 11 } } },
    title: { display: true, text: title, font: { size: 13 } },
    tooltip: {
      callbacks: {
        label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(1)}%`,
      },
    },
  },
  scales: {
    x: {
      type: 'time',
      time: {
        tooltipFormat: 'dd/MM/yyyy HH:mm:ss',
        displayFormats: { second: 'HH:mm:ss', minute: 'HH:mm', hour: 'dd/MM HH:mm', day: 'dd/MM' },
      },
      ...(xBounds.min ? { min: xBounds.min } : {}),
      ...(xBounds.max ? { max: xBounds.max } : {}),
      ticks: { maxTicksLimit: 12, font: { size: 10 } },
      grid: { display: false },
    },
    y: {
      min: 0,
      max: yMax,
      ticks: { callback: (v) => `${v}%`, font: { size: 10 } },
      grid: { color: 'rgba(0,0,0,0.04)' },
    },
  },
});

const AgentResourceChart = ({ agentId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(30);
  const [filterMode, setFilterMode] = useState('recent');
  const [dateRange, setDateRange] = useState(null);
  const [resourceInterval, setResourceInterval] = useState(30);
  const [settingInterval, setSettingInterval] = useState(false);
  const [latestTime, setLatestTime] = useState(null);
  const [autoExpanded, setAutoExpanded] = useState(false);
  const pollTimer = useRef(null);

  const fetchHistory = useCallback(async () => {
    if (!agentId) return;
    try {
      let params;
      if (filterMode === 'custom' && dateRange && dateRange[0] && dateRange[1]) {
        params = {
          start_date: dateRange[0].toISOString(),
          end_date: dateRange[1].toISOString(),
          limit: 2000,
        };
      } else {
        params = { minutes: timeRange, limit: 2000 };
      }
      const res = await agentsAPI.getResourceHistory(agentId, params);
      if (res?.success) {
        setHistory(res.data || []);
        if (res.latest_time) setLatestTime(res.latest_time);

        if (filterMode === 'recent' && (!res.data || res.data.length === 0) && res.latest_time && !autoExpanded) {
          const latestMs = parseTs(res.latest_time).getTime();
          const ageMins = (Date.now() - latestMs) / 60000;
          const expandTo = TIME_RANGES.find(r => r.value > ageMins);
          if (expandTo && expandTo.value > timeRange) {
            setAutoExpanded(true);
            setTimeRange(expandTo.value);
            return;
          }
        }
      }
    } catch (err) {
      console.warn('Failed to fetch resource history:', err.message);
    } finally {
      setLoading(false);
    }
  }, [agentId, timeRange, filterMode, dateRange, autoExpanded]);

  useEffect(() => {
    setLoading(true);
    fetchHistory();

    if (filterMode === 'recent') {
      const refreshMs = Math.max(timeRange <= 15 ? 5000 : timeRange <= 60 ? 15000 : 30000, 5000);
      pollTimer.current = setInterval(fetchHistory, refreshMs);
    }

    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, [fetchHistory, timeRange, filterMode]);

  useEffect(() => {
    if (!agentId || filterMode !== 'recent') return;

    const handler = (data) => {
      if (data.agent_id !== agentId) return;
      const la = data.system?.loadavg || [];
      const dio = data.disk_io || {};
      const point = {
        cpu_percent: data.cpu?.usage_percent || 0,
        memory_percent: data.memory?.usage_percent || 0,
        disk_percent: data.disk?.usage_percent || 0,
        memory_used_mb: data.memory?.used_mb || 0,
        disk_used_gb: data.disk?.used_gb || 0,
        loadavg_1m: data.system?.loadavg_1m ?? la[0] ?? null,
        loadavg_5m: data.system?.loadavg_5m ?? la[1] ?? null,
        loadavg_15m: data.system?.loadavg_15m ?? la[2] ?? null,
        disk_read_bps: dio.disk_read_bps ?? null,
        disk_write_bps: dio.disk_write_bps ?? null,
        timestamp: data.timestamp || new Date().toISOString(),
      };
      setHistory((prev) => {
        const cutoffMs = Date.now() - timeRange * 60 * 1000;
        const filtered = prev.filter((p) => {
          const ts = p.timestamp;
          const t = ts && !ts.endsWith('Z') && !ts.includes('+')
            ? new Date(ts.replace(' ', 'T') + 'Z').getTime()
            : new Date(ts).getTime();
          return t > cutoffMs;
        });
        return [...filtered, point];
      });
    };

    const socket = socketService.getSocket();
    if (socket) socket.on('agent-resources-update', handler);

    const removeConnect = socketService.onConnect(() => {
      const s = socketService.getSocket();
      if (s) s.on('agent-resources-update', handler);
    });

    return () => {
      const s = socketService.getSocket();
      if (s) s.off('agent-resources-update', handler);
      removeConnect();
    };
  }, [agentId, timeRange, filterMode]);

  const handleSetInterval = async () => {
    if (!resourceInterval || resourceInterval < 1) return;
    setSettingInterval(true);
    try {
      await agentsAPI.setResourceInterval(agentId, resourceInterval);
      message.success(`Resource interval set to ${resourceInterval}s`);
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to set interval');
    } finally {
      setSettingInterval(false);
    }
  };

  const handleFilterModeChange = (mode) => {
    setFilterMode(mode);
    if (mode === 'recent') {
      setDateRange(null);
    }
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  const handleCustomSearch = () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.warning('Pilih rentang tanggal terlebih dahulu');
      return;
    }
    setLoading(true);
    fetchHistory();
  };

  const toLoadChartValue = (v) => {
    if (v == null || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const historyWithLoad = history.filter(
    (h) => toLoadChartValue(h.loadavg_1m) != null
      || toLoadChartValue(h.loadavg_5m) != null
      || toLoadChartValue(h.loadavg_15m) != null
  );

  const hasDiskIoPoint = (h) => {
    const r = Number(h.disk_read_bps);
    const w = Number(h.disk_write_bps);
    return (Number.isFinite(r) && r >= 0) || (Number.isFinite(w) && w >= 0);
  };

  const historyWithDiskIo = history.filter(hasDiskIoPoint);

  const timestamps = history.map((h) => parseTs(h.timestamp));
  const loadTimestamps = historyWithLoad.map((h) => parseTs(h.timestamp));
  const diskIoTimestamps = historyWithDiskIo.map((h) => parseTs(h.timestamp));

  const cpuData = {
    labels: timestamps,
    datasets: [
      {
        label: 'CPU',
        data: history.map((h) => h.cpu_percent),
        borderColor: '#1890ff',
        backgroundColor: 'rgba(24,144,255,0.1)',
        fill: true,
        tension: 0.3,
        pointRadius: history.length > 100 ? 0 : 2,
        borderWidth: 1.5,
      },
    ],
  };

  const memoryData = {
    labels: timestamps,
    datasets: [
      {
        label: 'Memory',
        data: history.map((h) => h.memory_percent),
        borderColor: '#faad14',
        backgroundColor: 'rgba(250,173,20,0.1)',
        fill: true,
        tension: 0.3,
        pointRadius: history.length > 100 ? 0 : 2,
        borderWidth: 1.5,
      },
    ],
  };

  const diskData = {
    labels: timestamps,
    datasets: [
      {
        label: 'Disk',
        data: history.map((h) => h.disk_percent),
        borderColor: '#ff4d4f',
        backgroundColor: 'rgba(255,77,79,0.1)',
        fill: true,
        tension: 0.3,
        pointRadius: history.length > 100 ? 0 : 2,
        borderWidth: 1.5,
      },
    ],
  };

  const loadMax = history.reduce((max, h) => {
    const vals = [h.loadavg_1m, h.loadavg_5m, h.loadavg_15m].filter((v) => v != null && !Number.isNaN(v));
    const localMax = vals.length ? Math.max(...vals) : 0;
    return Math.max(max, localMax);
  }, 0);

  const loadPointRadius = historyWithLoad.length > 80 ? 0 : historyWithLoad.length < 8 ? 5 : 2;

  const loadData = {
    labels: loadTimestamps,
    datasets: [
      {
        label: '1 min',
        data: historyWithLoad.map((h) => toLoadChartValue(h.loadavg_1m)),
        borderColor: '#1890ff',
        backgroundColor: 'rgba(24,144,255,0.05)',
        fill: false,
        tension: 0.3,
        pointRadius: loadPointRadius,
        borderWidth: 2,
        spanGaps: false,
      },
      {
        label: '5 min',
        data: historyWithLoad.map((h) => toLoadChartValue(h.loadavg_5m)),
        borderColor: '#faad14',
        backgroundColor: 'rgba(250,173,20,0.05)',
        fill: false,
        tension: 0.3,
        pointRadius: loadPointRadius,
        borderWidth: 2,
        spanGaps: false,
      },
      {
        label: '15 min',
        data: historyWithLoad.map((h) => toLoadChartValue(h.loadavg_15m)),
        borderColor: '#52c41a',
        backgroundColor: 'rgba(82,196,26,0.05)',
        fill: false,
        tension: 0.3,
        pointRadius: loadPointRadius,
        borderWidth: 2,
        spanGaps: false,
      },
    ],
  };

  const loadPointsCount = historyWithLoad.length;
  const loadPointsMissing = history.length - loadPointsCount;

  const diskIoPointRadius = historyWithDiskIo.length > 80 ? 0 : historyWithDiskIo.length < 8 ? 5 : 2;

  const diskIoMax = historyWithDiskIo.reduce((max, h) => {
    const r = bytesPerSecToMb(h.disk_read_bps) || 0;
    const w = bytesPerSecToMb(h.disk_write_bps) || 0;
    return Math.max(max, r, w);
  }, 0);

  const diskIoData = {
    labels: diskIoTimestamps,
    datasets: [
      {
        label: 'Read',
        data: historyWithDiskIo.map((h) => bytesPerSecToMb(h.disk_read_bps)),
        borderColor: '#1890ff',
        backgroundColor: 'rgba(24,144,255,0.08)',
        fill: true,
        tension: 0.3,
        pointRadius: diskIoPointRadius,
        borderWidth: 2,
        spanGaps: false,
      },
      {
        label: 'Write',
        data: historyWithDiskIo.map((h) => bytesPerSecToMb(h.disk_write_bps)),
        borderColor: '#ff4d4f',
        backgroundColor: 'rgba(255,77,79,0.08)',
        fill: true,
        tension: 0.3,
        pointRadius: diskIoPointRadius,
        borderWidth: 2,
        spanGaps: false,
      },
    ],
  };

  const diskIoPointsCount = historyWithDiskIo.length;
  const diskIoPointsMissing = history.length - diskIoPointsCount;

  const combinedData = {
    labels: timestamps,
    datasets: [
      {
        label: 'CPU',
        data: history.map((h) => h.cpu_percent),
        borderColor: '#1890ff',
        backgroundColor: 'rgba(24,144,255,0.05)',
        fill: false,
        tension: 0.3,
        pointRadius: 0,
        borderWidth: 2,
      },
      {
        label: 'Memory',
        data: history.map((h) => h.memory_percent),
        borderColor: '#faad14',
        backgroundColor: 'rgba(250,173,20,0.05)',
        fill: false,
        tension: 0.3,
        pointRadius: 0,
        borderWidth: 2,
      },
      {
        label: 'Disk',
        data: history.map((h) => h.disk_percent),
        borderColor: '#ff4d4f',
        backgroundColor: 'rgba(255,77,79,0.05)',
        fill: false,
        tension: 0.3,
        pointRadius: 0,
        borderWidth: 2,
      },
    ],
  };

  const xBounds = (() => {
    if (history.length === 0) return {};
    if (filterMode === 'custom') return {};
    const firstTs = timestamps[0];
    const lastTs = timestamps[timestamps.length - 1];
    const now = new Date();
    const dataAge = now - lastTs;
    if (dataAge > 60000) {
      const padding = (lastTs - firstTs) * 0.02 || 30000;
      return { min: new Date(firstTs.getTime() - padding), max: new Date(lastTs.getTime() + padding) };
    }
    return { min: new Date(now.getTime() - timeRange * 60000), max: now };
  })();

  const chartOpts = (title) => makeChartOptions(title, 100, xBounds);
  const loadChartOpts = makeLoadChartOptions(
    'Load Average (1m / 5m / 15m)',
    xBounds,
    loadMax > 0 ? Math.ceil(loadMax * 1.2 * 10) / 10 : undefined
  );
  const diskIoChartOpts = makeDiskIoChartOptions(
    'Disk I/O (Read / Write)',
    xBounds,
    diskIoMax > 0 ? Math.ceil(diskIoMax * 1.25 * 1000) / 1000 : undefined
  );

  const rangeLabel = filterMode === 'custom' && dateRange?.[0] && dateRange?.[1]
    ? `${dateRange[0].format('DD/MM/YYYY HH:mm')} - ${dateRange[1].format('DD/MM/YYYY HH:mm')}`
    : null;

  if (loading && history.length === 0) {
    return (
      <Card title={<Space><LineChartOutlined /> Resource Charts</Space>}>
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
          <p style={{ marginTop: 16, color: '#888' }}>Loading resource history...</p>
        </div>
      </Card>
    );
  }

  return (
    <div>
      <Card
        size="small"
        title={<Space><LineChartOutlined /> Resource Charts</Space>}
        style={{ marginBottom: 16 }}
      >
        {/* Filter Controls */}
        <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <Select
            value={filterMode}
            onChange={handleFilterModeChange}
            size="small"
            style={{ width: 130 }}
            options={[
              { label: <Space><ClockCircleOutlined /> Realtime</Space>, value: 'recent' },
              { label: <Space><SearchOutlined /> Custom Range</Space>, value: 'custom' },
            ]}
          />

          {filterMode === 'recent' && (
            <Select
              value={timeRange}
              onChange={(val) => { setAutoExpanded(false); setTimeRange(val); }}
              options={TIME_RANGES.map((r) => ({ label: r.label, value: r.value }))}
              style={{ width: 110 }}
              size="small"
            />
          )}

          {filterMode === 'custom' && (
            <>
              <RangePicker
                showTime={{ format: 'HH:mm' }}
                format="DD/MM/YYYY HH:mm"
                size="small"
                value={dateRange}
                onChange={handleDateRangeChange}
                style={{ width: 340 }}
              />
              <Button
                size="small"
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleCustomSearch}
              >
                Tampilkan
              </Button>
            </>
          )}

          <Divider type="vertical" style={{ height: 24 }} />

          <Tooltip title="Set agent resource check interval (seconds)">
            <Space size={4}>
              <SettingOutlined />
              <InputNumber
                min={1}
                max={3600}
                value={resourceInterval}
                onChange={setResourceInterval}
                size="small"
                style={{ width: 65 }}
              />
              <span style={{ fontSize: 12 }}>s</span>
              <Button
                size="small"
                type="primary"
                icon={<SyncOutlined />}
                loading={settingInterval}
                onClick={handleSetInterval}
              >
                Set
              </Button>
            </Space>
          </Tooltip>
        </div>

        {autoExpanded && history.length > 0 && (
          <Alert
            type="info"
            showIcon
            closable
            message={`Agent sedang offline. Range diperluas ke ${TIME_RANGES.find(r => r.value === timeRange)?.label || timeRange + ' min'} untuk menampilkan data terakhir${latestTime ? ' (' + parseTs(latestTime).toLocaleString('id-ID') + ')' : ''}.`}
            style={{ marginBottom: 12 }}
          />
        )}

        {history.length === 0 ? (
          <Empty description={
            filterMode === 'custom'
              ? 'Tidak ada data pada rentang waktu yang dipilih.'
              : latestTime
                ? `Tidak ada data dalam ${TIME_RANGES.find(r => r.value === timeRange)?.label || timeRange + ' min'} terakhir. Data terakhir: ${parseTs(latestTime).toLocaleString('id-ID')}.`
                : 'Belum ada data resource history. Tunggu agent mengirim data.'
          } />
        ) : (
          <>
            {rangeLabel && (
              <div style={{ textAlign: 'center', marginBottom: 8, fontSize: 12, color: '#666' }}>
                Menampilkan data: <strong>{rangeLabel}</strong>
              </div>
            )}

            <div style={{ height: 250, marginBottom: 16 }}>
              <Line data={combinedData} options={chartOpts('All Resources')} />
            </div>

            {loadPointsCount > 0 && loadPointsMissing > 0 && (
              <Alert
                type="info"
                showIcon
                closable
                style={{ marginBottom: 12 }}
                message="Grafik load baru terisi untuk snapshot terbaru"
                description={`${loadPointsMissing} titik lama di rentang ini tidak punya data load (sebelum fitur ini). Tunggu ~5–15 menit atau pilih rentang lebih pendek — garis akan terisi otomatis.`}
              />
            )}

            {loadPointsCount === 0 ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message="Belum ada data load average di history"
                description="Pastikan agent & backend sudah di-restart setelah update. Data load disimpan tiap agent kirim resource (~30 detik)."
              />
            ) : (
              <div style={{ height: 220, marginBottom: 16 }}>
                <Line data={loadData} options={loadChartOpts} />
                {loadPointsCount < 3 && (
                  <div style={{ textAlign: 'center', fontSize: 11, color: '#999', marginTop: 4 }}>
                    {loadPointsCount} titik load — grafik akan lebih lengkap setelah beberapa kali pengiriman agent
                  </div>
                )}
              </div>
            )}

            {diskIoPointsCount > 0 && diskIoPointsMissing > 0 && (
              <Alert
                type="info"
                showIcon
                closable
                style={{ marginBottom: 12 }}
                message="Grafik Disk I/O baru terisi untuk snapshot terbaru"
                description={`${diskIoPointsMissing} titik lama tidak punya data disk I/O. Tunggu beberapa interval resource (~30 detik).`}
              />
            )}

            {diskIoPointsCount === 0 ? (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Belum ada data Disk I/O di history"
                description="Restart agent & backend setelah update, lalu tunggu agent mengirim resource. Linux/Windows didukung."
              />
            ) : (
              <div style={{ height: 220, marginBottom: 16 }}>
                <Line data={diskIoData} options={diskIoChartOpts} />
              </div>
            )}

            <Row gutter={16}>
              <Col xs={24} md={8}>
                <div style={{ height: 200 }}>
                  <Line data={cpuData} options={chartOpts('CPU Usage')} />
                </div>
              </Col>
              <Col xs={24} md={8}>
                <div style={{ height: 200 }}>
                  <Line data={memoryData} options={chartOpts('Memory Usage')} />
                </div>
              </Col>
              <Col xs={24} md={8}>
                <div style={{ height: 200 }}>
                  <Line data={diskData} options={chartOpts('Disk Usage')} />
                </div>
              </Col>
            </Row>

            <div style={{ textAlign: 'right', fontSize: 11, color: '#999', marginTop: 8 }}>
              {history.length} data points &middot; Last update: {history.length > 0 ? parseTs(history[history.length - 1].timestamp).toLocaleString('id-ID') : '-'}
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default AgentResourceChart;
